import { formatDate } from '@common/paymentsUtils/dateUtils';
import notificationTypes from '@common/constants/notificationTypes';
import { BANNERS } from '../constants/BannerConstants';
import {
  ACCOUNT_LINK_SUCCESS,
  ADDRESS_VERIFICATION_ERROR,
  CONTINUE_PAYMENT_SETUP,
  COVERAGE_REQUEST_ERROR,
  DEFAULT_ERROR,
  DEPOSIT_ACCOUNT_SET,
  DEPOSIT_ACCOUNT_UPDATED,
  EMERGENCY_VERIFICATION_NEEDED,
  EMERGENCY_VERIFICATION_SUBMITTED,
  HOSTED_ONBOARDING_COMPLETED,
  HOSTED_ONBOARDING_REQUIRED,
  ID_VERIFICATION_UPDATED,
  IN_BETWEEN_TERMS,
  INVALID_ACCOUNT_INFO,
  INVITE_NEW_RENTER,
  INVITE_NEW_RENTER_SUCCESS,
  INVITE_RENTER_SUCCESS,
  MOVE_IN_COSTS_CANCEL_SUCCESS,
  MOVE_IN_COSTS_REQUEST_SUCCESS,
  MOVE_IN_COSTS_SETTINGS_UPDATED,
  ONBOARDING_COMPLETE,
  ONE_TIME_PAYMENT_CANCEL_SUCCESS,
  ONE_TIME_PAYMENT_REQUEST_SUCCESS,
  PAYMENT_LENGTH_UPDATED,
  PAYMENT_PLAN_CANCEL_SUCCESS,
  PAYMENT_PLAN_REQUEST_SUCCESS,
  PAYMENT_SETTINGS_COMPLETE,
  PAYMENT_SETTINGS_UPDATED,
  PHONE_VERIFICATION_SEND_ERROR,
  REMIND_RENTER_SUCCESS,
  RENT_AMOUNT_UPDATE_CANCEL_SUCCESS,
  RENT_AMOUNT_UPDATE_FAIL,
  RENT_AMOUNT_UPDATE_SUCCESS,
  RENTAL_PROTECTION_COVERAGE_REQUEST_NOT_SIGNED,
  RENTAL_PROTECTION_FIRST_RENT_PAYMENT_FAILED,
  RENTAL_PROTECTION_LATEST_RENT_PAYMENT_FAILED,
  RENTAL_PROTECTION_MISSED_RENT_PAYMENT_BANNER,
  RENTAL_PROTECTION_POLICY_PENDING,
  STOP_PAYMENTS_SUCCESS,
  STRIPE_CONTACT_INFO_UPDATE,
  SWITCH_TO_M2M,
  TAX_ID_ERROR,
  TENANT_INFO_UPDATE_FAIL,
  TENANT_INFO_UPDATED,
} from '../constants/NotificationConstants';
import {
  ConfirmBusinessType,
  ContinuePaymentSetup as ContinuePaymentSetupComponent,
  CoverageRequestError,
  HostedOnboardingRequired as HostedOnboardingRequiredComponent,
  InBetweenTerms,
  InviteNewRenter as InviteNewRenterComponent,
  PaymentTermEligibleForRentalProtectionPolicy,
  RentalProtectionCoverageRequestNotSigned,
  RentalProtectionFirstRentPaymentFailed,
  RentalProtectionLatestRentPaymentFailed,
  RentalProtectionMissedRentPayment,
  RentalProtectionPolicyPending,
} from './customNotifications';

export default {
  [DEFAULT_ERROR]: {
    type: notificationTypes.ERROR,
    text: ({ subject, verb = 'saved' }: { subject?: string; verb?: string } = {}): string =>
      subject ? `${subject} couldn’t be ${verb}. Try again.` : 'That couldn’t be completed. Try again.',
  },
  [MOVE_IN_COSTS_SETTINGS_UPDATED]: {
    type: notificationTypes.SUCCESS,
    text: (): string => 'Your move-in costs settings have been updated',
  },
  [CONTINUE_PAYMENT_SETUP]: {
    custom: true,
    component: ContinuePaymentSetupComponent,
  },
  [PAYMENT_SETTINGS_UPDATED]: {
    type: notificationTypes.SUCCESS,
    text: (): string => 'Your payment settings have been updated',
  },
  [PAYMENT_SETTINGS_COMPLETE]: {
    type: notificationTypes.SUCCESS,
    text: (): string => 'Payment settings complete. You’re nearly ready to start collecting payments.',
  },
  [ACCOUNT_LINK_SUCCESS]: {
    type: notificationTypes.SUCCESS,
    text: (): string => 'Your account has been linked',
  },
  [DEPOSIT_ACCOUNT_SET]: {
    type: notificationTypes.SUCCESS,
    text: (): string => 'Your deposit account has been set for all of your properties',
  },
  [DEPOSIT_ACCOUNT_UPDATED]: {
    type: notificationTypes.SUCCESS,
    text: (): string => 'Your deposit account has been updated for all of your properties',
  },
  [INVALID_ACCOUNT_INFO]: {
    type: notificationTypes.ERROR,
    text: (): string => 'The account information you entered is invalid. Try another one.',
  },
  [ID_VERIFICATION_UPDATED]: {
    type: notificationTypes.SUCCESS,
    text: (): string => 'Your ID verification info has been updated',
  },
  [TAX_ID_ERROR]: {
    type: notificationTypes.ERROR,
    text: (): string => 'The Employer Identification Number you entered is invalid. Try again.',
  },
  [PHONE_VERIFICATION_SEND_ERROR]: {
    type: notificationTypes.ERROR,
    text: (): string => 'The verification code couldn’t be sent to this number. Wait and try again.',
  },
  [TENANT_INFO_UPDATED]: {
    type: notificationTypes.SUCCESS,
    text: (): string => 'Your tenant’s info has been updated',
  },
  [TENANT_INFO_UPDATE_FAIL]: {
    type: notificationTypes.ERROR,
    text: (): string => 'Your tenant’s information couldn’t be updated. Wait and try again.',
  },
  [STRIPE_CONTACT_INFO_UPDATE]: {
    type: notificationTypes.SUCCESS,
    text: (): string => 'We’ve updated your contact information on file with Stripe',
  },
  [ONBOARDING_COMPLETE]: {
    type: notificationTypes.SUCCESS,
    text: ({ tenantFirstName }: Record<string, string>): string =>
      `We invited ${tenantFirstName} to set up online payments. ` +
      'Let them know so they don’t miss the email, and we’ll notify you once they finish their setup.',
  },
  [INVITE_RENTER_SUCCESS]: {
    type: notificationTypes.SUCCESS,
    text: ({ tenantEmail }: Record<string, string>): string => `An invitation has been sent to ${tenantEmail}`,
  },
  [EMERGENCY_VERIFICATION_NEEDED]: {
    type: notificationTypes.DEFAULT,
    text: (): string => 'We’re missing your identity verification information. ' + 'Enter it now to continue.',
  },
  [EMERGENCY_VERIFICATION_SUBMITTED]: {
    type: notificationTypes.SUCCESS,
    text: (): string => 'ID verification info saved',
  },
  [HOSTED_ONBOARDING_REQUIRED]: {
    custom: true,
    component: HostedOnboardingRequiredComponent,
  },
  [HOSTED_ONBOARDING_COMPLETED]: {
    type: notificationTypes.SUCCESS,
    text: (): string => 'We’ve received your information',
  },
  [RENT_AMOUNT_UPDATE_SUCCESS]: {
    type: notificationTypes.SUCCESS,
    text: ({ tenantFirstName }: Record<string, string>): string =>
      'You’ve started a change to your rent ' + `amount. We sent ${tenantFirstName} a notice.`,
  },
  [RENT_AMOUNT_UPDATE_FAIL]: {
    type: notificationTypes.ERROR,
    text: (): string => 'Your rent amount couldn’t be updated. Wait and try again.',
  },
  [RENT_AMOUNT_UPDATE_CANCEL_SUCCESS]: {
    type: notificationTypes.SUCCESS,
    text: (): string => 'Your rent amount update has been canceled',
  },
  [PAYMENT_LENGTH_UPDATED]: {
    type: notificationTypes.SUCCESS,
    text: ({ paymentEndDate }: Record<string, string>): string =>
      'Your payment end date has been changed to ' +
      `${formatDate(paymentEndDate, 'MMMM d')}. We’ll notify your tenant.`,
  },
  [SWITCH_TO_M2M]: {
    type: notificationTypes.SUCCESS,
    text: (): string => 'You’ve switched to month-to-month payments with no end date. We’ll notify your tenant.',
  },
  [STOP_PAYMENTS_SUCCESS]: {
    type: notificationTypes.SUCCESS,
    text: (): string => 'You have stopped payments that aren’t in progress. We’ll notify your tenant.',
  },
  [INVITE_NEW_RENTER]: {
    custom: true,
    component: InviteNewRenterComponent,
  },
  [INVITE_NEW_RENTER_SUCCESS]: {
    type: notificationTypes.SUCCESS,
    text: ({ tenantFirstName }: Record<string, string>): string =>
      `We invited ${tenantFirstName} to set up online payments. ` + 'Let them know so they don’t miss the email.',
  },
  [ONE_TIME_PAYMENT_REQUEST_SUCCESS]: {
    type: notificationTypes.SUCCESS,
    text: ({ tenantFirstName }: Record<string, string>): string => `We sent ${tenantFirstName} a payment request`,
  },
  [ONE_TIME_PAYMENT_CANCEL_SUCCESS]: {
    type: notificationTypes.SUCCESS,
    text: ({ tenantFirstName }: Record<string, string>): string =>
      `Your one-time payment has been canceled. We sent ${tenantFirstName} a notice.`,
  },
  [PAYMENT_PLAN_REQUEST_SUCCESS]: {
    type: notificationTypes.SUCCESS,
    text: ({ tenantName }: Record<string, string>): string =>
      `We sent ${tenantName} a payment plan installment request`,
  },
  [PAYMENT_PLAN_CANCEL_SUCCESS]: {
    type: notificationTypes.SUCCESS,
    text: ({ tenantFirstName, paymentPlanInstallmentTitle }: Record<string, string>): string =>
      `Your ${paymentPlanInstallmentTitle} has been canceled. We sent ${tenantFirstName} a notice.`,
  },
  [MOVE_IN_COSTS_REQUEST_SUCCESS]: {
    type: notificationTypes.SUCCESS,
    text: ({ tenantFirstName }: Record<string, string>): string => `We sent ${tenantFirstName} a payment request`,
  },
  [MOVE_IN_COSTS_CANCEL_SUCCESS]: {
    type: notificationTypes.SUCCESS,
    text: ({ tenantFirstName }: Record<string, string>): string =>
      `Your move-in costs payment has been canceled. We sent ${tenantFirstName} a notice.`,
  },
  [REMIND_RENTER_SUCCESS]: {
    type: notificationTypes.SUCCESS,
    text: ({ tenantEmail }: Record<string, string>): string => `A reminder has been sent to ${tenantEmail}`,
  },
  [IN_BETWEEN_TERMS]: {
    custom: true,
    component: InBetweenTerms,
  },
  [RENTAL_PROTECTION_FIRST_RENT_PAYMENT_FAILED]: {
    custom: true,
    component: RentalProtectionFirstRentPaymentFailed,
  },
  [RENTAL_PROTECTION_LATEST_RENT_PAYMENT_FAILED]: {
    custom: true,
    component: RentalProtectionLatestRentPaymentFailed,
  },
  [RENTAL_PROTECTION_COVERAGE_REQUEST_NOT_SIGNED]: {
    custom: true,
    component: RentalProtectionCoverageRequestNotSigned,
  },
  [RENTAL_PROTECTION_POLICY_PENDING]: {
    custom: true,
    component: RentalProtectionPolicyPending,
  },
  [RENTAL_PROTECTION_MISSED_RENT_PAYMENT_BANNER]: {
    custom: true,
    component: RentalProtectionMissedRentPayment,
  },
  [ADDRESS_VERIFICATION_ERROR]: {
    type: notificationTypes.ERROR,
    text: (): string => 'We couldn’t verify your address. Make sure it’s correct before you save it.',
  },
  [BANNERS.rentalProtectionPolicyEligibleForPayments]: {
    custom: true,
    component: PaymentTermEligibleForRentalProtectionPolicy,
  },
  [COVERAGE_REQUEST_ERROR]: {
    custom: true,
    component: CoverageRequestError,
  },
  [BANNERS.confirmBusinessType]: {
    custom: true,
    component: ConfirmBusinessType,
  },
};
